import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
    Box, CssBaseline, Typography, Avatar, IconButton,
    Stack, Button, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, TextField, InputAdornment, Menu, MenuItem,
    TablePagination, Breadcrumbs, Link, CircularProgress,
    Dialog, DialogTitle, Snackbar, Alert, Tabs, Tab
} from '@mui/material';
import {
    Search, MoreVert, Edit, CheckCircle, Cancel
} from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import Header from "../../component/header/Header";
import moment from "moment";

const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    filterBg: "#FFF9E6",
    accent: '#FFC107',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037',
    accentDark: '#FFA000',
    white: '#FFFFFF'
};

export default function LeaveScreen() {
    const navigate = useNavigate();

    // --- Data States ---
    const [leaveApplicationList, setLeaveApplicationList] = useState([]);

    // --- UI/Status States ---
    const [loading, setLoading] = useState(false);
    const [loadingApprove, setLoadingApprove] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filterSearch, setFilterSearch] = useState("");
    const [statusTab, setStatusTab] = useState("P");
    const [actionAnchorEl, setActionAnchorEl] = useState(null);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [pendingStatus, setPendingStatus] = useState(""); // Track 'C' or 'R'
    const [updateDialog, setUpdateDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // --- API Calls ---
    const getLeaveApplicationList = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/leave`);
            const resJson = await response.json();
            if (resJson.status === 1) {
                setLeaveApplicationList(resJson.data || []);
                console.log(resJson.data)
            } else {
                showSnackbar(resJson.message, "error");
            }
        } catch (error) {
            showSnackbar("Failed to connect to server", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    const handleApproveLeave = async () => {
        if (!selectedStaff || !pendingStatus) return;
        
        setLoadingApprove(true);
        try {
            const res = await fetch(`https://www.geniusmileniaeducation.link/db-api/leave/${selectedStaff.leaveId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    status: pendingStatus,
                    approveDate: moment().format("DD/MM/YYYY")
                })
            });
            const json = await res.json();
            if (json.status === 1) {
                showSnackbar(`Application ${pendingStatus === 'C' ? 'Approved' : 'Rejected'} successfully`, "success");
                setUpdateDialog(false);
                getLeaveApplicationList();
            } else {
                showSnackbar(json.message, "error");
            }
        } catch (err) {
            showSnackbar("Connection Error", "error");
        } finally {
            setLoadingApprove(false);
        }
    };

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    useEffect(() => {
        getLeaveApplicationList();
        localStorage.setItem("current_tab", "Staff Leave");
    }, [getLeaveApplicationList]);

    const handleActionOpen = (event, staff) => {
        setActionAnchorEl(event.currentTarget);
        setSelectedStaff(staff);
    };

    const handleActionClose = () => {
        setActionAnchorEl(null);
    };

    const filteredData = useMemo(() => {
        const searchStr = filterSearch.toUpperCase();
        return leaveApplicationList.filter(item => {
            const matchesStatus = item.status === statusTab;
            const matchesSearch = (item.staffName?.toUpperCase().includes(searchStr)) ||
                (item.leaveDate?.toUpperCase().includes(searchStr));
            return matchesStatus && matchesSearch;
        });
    }, [leaveApplicationList, filterSearch, statusTab]);

    const paginatedData = useMemo(() => {
        return filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [filteredData, page, rowsPerPage]);

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />

            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: colors.white, m: 2, borderRadius: 5 }}>

                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigate("/Portal")} sx={{ cursor: 'pointer' }}>
                        Portal
                    </Link>
                    <Typography color="text.primary">Staff Leave</Typography>
                </Breadcrumbs>

                <Header title="Staff Leave" subtitle="Manage staff leave application" />

                <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: colors.filterBg, borderRadius: 3 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
                        <Tabs
                            value={statusTab}
                            onChange={(e, val) => { setStatusTab(val); setPage(0); }}
                            sx={{
                                '& .MuiTabs-indicator': { bgcolor: colors.accentDark },
                                '& .MuiTab-root.Mui-selected': { color: colors.accentDark, fontWeight: 'bold' }
                            }}
                        >
                            <Tab label="Pending Approval" value="P" />
                            <Tab label="Approved" value="C" />
                            <Tab label="Rejected" value="R" />
                        </Tabs>

                        <TextField
                            placeholder="Search staff name..."
                            size="small"
                            value={filterSearch}
                            onChange={(e) => { setFilterSearch(e.target.value); setPage(0); }}
                            sx={{ width: 350, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#F8F9FA' } }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                            }}
                        />
                    </Stack>
                </Paper>

                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #F0E6CC', borderRadius: 4, maxHeight: '55vh' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Staff Name</TableCell>
                                <TableCell sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Leave Date</TableCell>
                                <TableCell sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Reason</TableCell>
                                <TableCell sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Status</TableCell>
                                <TableCell align="right" sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                        <CircularProgress size={30} sx={{ color: colors.accentDark }} />
                                    </TableCell>
                                </TableRow>
                            ) : paginatedData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>No Records Found</TableCell>
                                </TableRow>
                            ) : paginatedData.map((row) => (
                                <TableRow key={row.missingId} hover>
                                    <TableCell>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar sx={{ bgcolor: colors.tableHeader, color: colors.textMain, fontSize: 14, fontWeight: 'bold' }}>
                                                {row.staffName?.charAt(0)}
                                            </Avatar>
                                            <Typography variant="subtitle2" fontWeight="bold">{row.staffName}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{row.leaveDate || '-'}</TableCell>
                                    <TableCell>{row.reason || '-'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.status === "C" ? "Approved" : row.status === "R" ? "Rejected" : "Pending"}
                                            size="small"
                                            sx={{
                                                borderRadius: 1.5,
                                                bgcolor: row.status === "C" ? '#E8F5E9' : row.status === "R" ? '#FFEBEE' : '#FFF3E0',
                                                color: row.status === "C" ? '#2E7D32' : row.status === "R" ? '#C62828' : '#E65100',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        {row.status === "P" && (
                                            <IconButton size="small" onClick={(e) => handleActionOpen(e, row)}><MoreVert /></IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />

                {/* Action Menu */}
                <Menu anchorEl={actionAnchorEl} open={Boolean(actionAnchorEl)} onClose={handleActionClose}>
                    <MenuItem onClick={() => {
                        setPendingStatus("C");
                        setUpdateDialog(true);
                        handleActionClose();
                    }}>
                        <CheckCircle fontSize="small" sx={{ mr: 1, color: 'green' }} /> Approve
                    </MenuItem>
                    <MenuItem onClick={() => {
                        setPendingStatus("R");
                        setUpdateDialog(true);
                        handleActionClose();
                    }}>
                        <Cancel fontSize="small" sx={{ mr: 1, color: 'red' }} /> Reject
                    </MenuItem>
                </Menu>

                {/* --- UPDATE DIALOG --- */}
                <Dialog
                    open={updateDialog}
                    onClose={() => !loadingApprove && setUpdateDialog(false)}
                    PaperProps={{ sx: { borderRadius: 4, minWidth: 400 } }}
                >
                    <Box p={4} textAlign="center">
                        <DialogTitle sx={{ fontWeight: 'bold', p: 0, mb: 1 }}>
                            {pendingStatus === 'C' ? 'Approve Application?' : 'Reject Application?'}
                        </DialogTitle>
                        
                        <Typography variant="body2" sx={{ mb: 4, color: 'gray' }}>
                            Are you sure you want to <strong>{pendingStatus === 'C' ? 'approve' : 'reject'}</strong> leave for <br /> 
                            <strong>{selectedStaff?.staffName}</strong> on <strong>{selectedStaff?.leaveDate}</strong>?
                        </Typography>

                        <Stack direction="row" spacing={2} justifyContent="center">
                            <Button
                                onClick={() => setUpdateDialog(false)}
                                color="inherit"
                                variant="outlined"
                                disabled={loadingApprove}
                                sx={{ borderRadius: 2, textTransform: 'none', px: 4 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleApproveLeave}
                                variant="contained"
                                disabled={loadingApprove}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    color: 'white',
                                    bgcolor: pendingStatus === 'C' ? '#2E7D32' : '#C62828',
                                    px: 4,
                                    '&:hover': { bgcolor: pendingStatus === 'C' ? '#1B5E20' : '#B71C1C' }
                                }}
                            >
                                {loadingApprove ? <CircularProgress size={20} color="inherit" /> : "Confirm"}
                            </Button>
                        </Stack>
                    </Box>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </Box>
    );
}